import {useAui} from '@assistant-ui/react';
import {IconButton} from '@wandb/components';
import {DraggableHandle} from '@wandb/weave/components/DraggablePopups';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {useCallback} from 'react';
import {createPortal} from 'react-dom';
import Draggable from 'react-draggable';

import {useActiveThread} from '../../agentChat/store/context';
import {TOP_NAV_HEIGHT} from '../../util/constants';
import {AgentChatProvider, useAgentChatAvailability} from '../AgentChat';
import {useNavContext} from '../NavContextProvider';
import {useBoundedDrag} from './useBoundedDrag';
import {useNightModeRevertFilter} from './useNightModeRevertFilter';
import {WBAgentButton} from './WBAgentButton';
import {useWBAgentContextStore} from './WBAgentContextStore';
import {WBAgentThread} from './WBAgentThread';
import {WBAgentWindow} from './WBAgentWindow';

export const WBAgent = () => {
  return (
    <AgentChatProvider>
      <WBAgentInner />
    </AgentChatProvider>
  );
};
export const WBAgentInner = () => {
  const {isAvailable: isWorkspaceAgentEnabled} = useAgentChatAvailability();
  const {params} = useNavContext();
  const hasProjectContext = !!params.entityName && !!params.projectName;
  const aui = useAui();
  const activeThread = useActiveThread();
  const open = useWBAgentContextStore(s => s.open);
  const setOpen = useWBAgentContextStore(s => s.setOpen);
  const clearRuns = useWBAgentContextStore(s => s.clearRuns);
  const {containerRef, nodeRef, position, handleDrag} = useBoundedDrag(open);

  const nightModeRevertFilter = useNightModeRevertFilter();

  const handleClose = useCallback(() => setOpen(false), [setOpen]);
  const handleNewChat = useCallback(() => {
    aui.threads().switchToNewThread();
    clearRuns();
  }, [aui, clearRuns]);

  return (
    <>
      {isWorkspaceAgentEnabled && (
        <WBAgentButton
          onPress={() => setOpen(!open)}
          isLoading={activeThread?.status === 'running'}
        />
      )}
      {open &&
        isWorkspaceAgentEnabled &&
        createPortal(
          <Tailwind>
            <div /* Container to constrain the chat window's position within the page and below the navbar */
              ref={containerRef}
              className="fixed inset-x-0 bottom-0 z-[1000] pointer-events-none overflow-hidden"
              style={{top: TOP_NAV_HEIGHT}}>
              <Draggable
                handle=".handle"
                bounds="parent"
                nodeRef={nodeRef}
                position={position}
                onDrag={handleDrag}>
                <div /* Chat window container */
                  ref={nodeRef}
                  className="absolute top-0 left-0 pointer-events-auto rounded-[8px] flex flex-col">
                  <div /* Background layer to counter-invert the global night-mode filter so dark:colors render properly */
                    className="absolute inset-0 rounded-[8px] bg-white dark:bg-moon-850 border-[1.5px] border-moon-250 dark:border-[#363C44] shadow-[0_12px_24px_0_rgba(13,15,18,0.16)] dark:shadow-[0_12px_24px_0_rgba(13,15,18,0.32)]"
                    style={{filter: nightModeRevertFilter}}
                  />
                  <WBAgentWindow>
                    <DraggableHandle>
                      <div className="flex items-center justify-between border-b border-solid border-moon-250 px-24 py-12">
                        <h2 className="text-xl font-semibold leading-[40px]">
                          W&B agent
                        </h2>
                        <div>
                          <IconButton
                            variant="ghost"
                            icon="pencil-edit"
                            aria-label="new-chat"
                            onClick={handleNewChat}
                            tooltip={{
                              children: 'Start new chat (clears current)',
                            }}
                          />
                          <IconButton
                            variant="ghost"
                            icon="close"
                            aria-label="close"
                            onClick={handleClose}
                            tooltip={{
                              children: 'Minimize',
                            }}
                          />
                        </div>
                      </div>
                    </DraggableHandle>
                    <WBAgentThread showOpenProjectBanner={!hasProjectContext} />
                  </WBAgentWindow>
                </div>
              </Draggable>
            </div>
          </Tailwind>,
          document.body
        )}
    </>
  );
};
